import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { t } from '../../i18n/nl.js';
import { useToast } from '../../contexts/ToastContext';
import { describeError } from '../../utils/errorMessages.js';

const STATUSES = ['nieuw', 'in_behandeling', 'afgehandeld', 'afgewezen'];

function datumTijd(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' });
}

function persoonLabel(report, profiles) {
  const profile = profiles[report.reporter_id];
  return profile?.full_name || profile?.email || t('bugReports.onbekendeMelder');
}

export default function BugrapportenPage() {
  const { showSuccess, showError } = useToast();
  const [reports, setReports] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [selectedId, setSelectedId] = useState(null);
  const [status, setStatus] = useState('nieuw');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('bug_reports')
        .select('id, reporter_id, reporter_role, description, screen, action_type, error_reference, error_category, app_version, build_number, platform, os_version, network_state, occurred_at, status, handled_by, handled_at, handler_note, created_at')
        .order('created_at', { ascending: false });
      if (error) throw error;
      const rows = data || [];
      setReports(rows);
      const ids = [...new Set(rows.flatMap(row => [row.reporter_id, row.handled_by]).filter(Boolean))];
      if (ids.length) {
        const { data: people, error: peopleError } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', ids);
        if (peopleError) throw peopleError;
        setProfiles(Object.fromEntries((people || []).map(person => [person.id, person])));
      } else {
        setProfiles({});
      }
      setSelectedId(current => (current && rows.some(row => row.id === current) ? current : rows[0]?.id || null));
    } catch (error) {
      showError(describeError(error).message);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => reports.filter(report => {
    if (filter === 'alles') return true;
    if (filter === 'open') return ['nieuw', 'in_behandeling'].includes(report.status);
    return report.status === filter;
  }), [reports, filter]);

  const selected = reports.find(report => report.id === selectedId) || null;

  useEffect(() => {
    if (!selected) return;
    setStatus(selected.status);
    setNote(selected.handler_note || '');
  }, [selected]);

  async function save() {
    if (!selected) return;
    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('bug_reports')
        .update({ status, handler_note: note.trim() || null })
        .eq('id', selected.id)
        .select('id, status, handler_note, handled_by, handled_at')
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error(t('bugReports.nietOpgeslagen'));
      setReports(current => current.map(report => (report.id === data.id ? { ...report, ...data } : report)));
      showSuccess(t('bugReports.opgeslagen'));
    } catch (error) {
      showError(describeError(error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="page-header bug-report-page-header">
        <div>
          <h1>{t('bugReports.titel')}</h1>
          <p>{t('bugReports.subtitel')}</p>
        </div>
        <button className="btn btn-secondary" onClick={load} disabled={loading}>{t('bugReports.vernieuwen')}</button>
      </div>

      <div className="bug-report-filters" aria-label={t('bugReports.filterLabel')}>
        {['open', 'nieuw', 'in_behandeling', 'afgehandeld', 'afgewezen', 'alles'].map(item => (
          <button key={item} className={`btn ${filter === item ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(item)}>
            {t(`bugReports.filter.${item}`)}
            <span className="bug-report-count">{item === 'alles' ? reports.length : item === 'open' ? reports.filter(r => ['nieuw', 'in_behandeling'].includes(r.status)).length : reports.filter(r => r.status === item).length}</span>
          </button>
        ))}
      </div>

      {loading ? <div className="loading">{t('common.laden')}</div> : reports.length === 0 ? (
        <div className="empty-state"><h2>{t('bugReports.leegTitel')}</h2><p>{t('bugReports.leegTekst')}</p></div>
      ) : (
        <div className="bug-report-workspace">
          <div className="bug-report-list" aria-label={t('bugReports.lijstLabel')}>
            {filtered.map(report => (
              <button key={report.id} className={`bug-report-list-item${selectedId === report.id ? ' is-selected' : ''}`} onClick={() => setSelectedId(report.id)}>
                <span className={`bug-report-status status-${report.status}`}>{t(`bugReports.status.${report.status}`)}</span>
                <strong>{persoonLabel(report, profiles)}</strong>
                <span className="bug-report-summary">{report.description}</span>
                <small>{datumTijd(report.created_at)} · {report.screen || t('bugReports.schermOnbekend')}</small>
              </button>
            ))}
            {filtered.length === 0 && <p className="bug-report-filter-empty">{t('bugReports.filterLeeg')}</p>}
          </div>

          {selected && (
            <section className="bug-report-detail" aria-labelledby="bug-report-detail-title">
              <div className="bug-report-detail-heading">
                <div>
                  <span className={`bug-report-status status-${selected.status}`}>{t(`bugReports.status.${selected.status}`)}</span>
                  <h2 id="bug-report-detail-title">{persoonLabel(selected, profiles)}</h2>
                  <p>{datumTijd(selected.occurred_at || selected.created_at)}</p>
                </div>
                {selected.error_reference && <code>{selected.error_reference}</code>}
              </div>

              <div className="bug-report-description">{selected.description}</div>

              <dl className="bug-report-context">
                <div><dt>{t('bugReports.scherm')}</dt><dd>{selected.screen || '—'}</dd></div>
                <div><dt>{t('bugReports.actie')}</dt><dd>{selected.action_type || '—'}</dd></div>
                <div><dt>{t('bugReports.categorie')}</dt><dd>{selected.error_category || '—'}</dd></div>
                <div><dt>{t('bugReports.platform')}</dt><dd>{[selected.platform, selected.os_version].filter(Boolean).join(' · ') || '—'}</dd></div>
                <div><dt>{t('bugReports.versie')}</dt><dd>{[selected.app_version, selected.build_number && t('bugReports.buildNummer', { nummer: selected.build_number })].filter(Boolean).join(' · ') || '—'}</dd></div>
                <div><dt>{t('bugReports.netwerk')}</dt><dd>{selected.network_state || '—'}</dd></div>
              </dl>

              <div className="form-group">
                <label className="form-label" htmlFor="bug-status">{t('bugReports.statusLabel')}</label>
                <select id="bug-status" className="form-input" value={status} onChange={event => setStatus(event.target.value)}>
                  {STATUSES.map(item => <option key={item} value={item}>{t(`bugReports.status.${item}`)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="bug-note">{t('bugReports.notitieLabel')}</label>
                <textarea id="bug-note" className="form-input bug-report-note" maxLength={2000} value={note} onChange={event => setNote(event.target.value)} placeholder={t('bugReports.notitiePlaceholder')} />
                <small className="form-hint">{t('bugReports.notitieHint')}</small>
              </div>
              {selected.handled_at && <p className="bug-report-handled">{t('bugReports.laatstBehandeld', { datum: datumTijd(selected.handled_at), naam: profiles[selected.handled_by]?.full_name || t('bugReports.beheerder') })}</p>}
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? t('bugReports.opslaanBezig') : t('bugReports.opslaan')}</button>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
